import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCourse, enrollCourse, downloadEbook, downloadEbookFile, downloadChapterFile } from '../../services/api';
import { useAuth } from '../../context/AuthContext.jsx';
import { toast } from 'react-toastify';
import { FaClock, FaBook, FaPlay, FaCheck, FaDownload, FaBookOpen, FaBookReader, FaArrowLeft } from 'react-icons/fa';
import FlipbookReader from '../../components/FlipbookReader.jsx';
import './CourseDetail.css';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [ebookUrl, setEbookUrl] = useState(null);
  const [readerChapters, setReaderChapters] = useState([]);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  // Cleanup blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (ebookUrl && ebookUrl.startsWith('blob:')) {
        URL.revokeObjectURL(ebookUrl);
        console.log('🗑️ Revoked blob URL');
      }
    };
  }, [ebookUrl]);

  useEffect(() => {
    return () => {
      readerChapters.forEach((chapter) => {
        if (chapter.pdfFile && chapter.pdfFile.startsWith('blob:')) {
          URL.revokeObjectURL(chapter.pdfFile);
        }
      });
    };
  }, [readerChapters]);

  const createPdfBlobUrl = (response) => {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  };

  const revokeReaderUrls = () => {
    if (ebookUrl && ebookUrl.startsWith('blob:')) {
      URL.revokeObjectURL(ebookUrl);
    }

    readerChapters.forEach((chapter) => {
      if (chapter.pdfFile && chapter.pdfFile.startsWith('blob:')) {
        URL.revokeObjectURL(chapter.pdfFile);
      }
    });

    setEbookUrl(null);
    setReaderChapters([]);
  };

  const triggerBlobDownload = (blobUrl, filename) => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchCourse = async () => {
    try {
      const res = await getCourse(id);
      setCourse(res.data.course);
      setIsEnrolled(res.data.isEnrolled);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      toast.info('Please login to enroll');
      navigate('/login');
      return;
    }

    setEnrolling(true);
    try {
      const res = await enrollCourse(id);
      toast.success('Successfully enrolled in course!');

      // For ebooks, stay on the page and refresh course data to show buttons
      if (course.courseType === 'ebook') {
        // Fetch fresh course data to get updated enrollment status
        await fetchCourse();
      } else {
        // For regular courses, navigate to learn page
        navigate(`/courses/${id}/learn`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const handleReadOnline = async () => {
    try {
      // Verify enrollment
      await downloadEbook(id);

      // Check if this is a multi-chapter ebook or single-file ebook
      if (course.chapters && course.chapters.length > 0) {
        revokeReaderUrls();
        const chaptersWithBlobUrls = await Promise.all(
          course.chapters.map(async (chapter) => {
            const response = await downloadChapterFile(id, chapter._id);
            return {
              ...chapter,
              pdfFile: createPdfBlobUrl(response)
            };
          })
        );
        setReaderChapters(chaptersWithBlobUrls);
        // Multi-chapter ebook - FlipbookReader will handle chapters directly with Firebase URLs
        console.log('📚 Opening multi-chapter ebook with', course.chapters.length, 'chapters');
        setShowFlipbook(true);
      } else if (course.ebookFile) {
        revokeReaderUrls();
        // Legacy single-file ebook - fetch as blob
        const firebaseUrl = course.ebookFile;
        console.log('🔍 Fetching single PDF from Firebase:', firebaseUrl);

        const response = await downloadEbookFile(id);
        if (!response.data) {
          throw new Error('Failed to fetch PDF');
        }

        const blobUrl = createPdfBlobUrl(response);
        console.log('✅ Created blob URL for PDF:', blobUrl);

        setEbookUrl(blobUrl);
        setShowFlipbook(true);
      } else {
        throw new Error('No ebook file found');
      }
    } catch (error) {
      console.error('❌ PDF fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to load ebook');
    }
  };

  const handleDownloadEbook = async () => {
    try {
      // First verify enrollment
      await downloadEbook(id);

      // Check if this is a multi-chapter ebook or single-file ebook
      if (course.chapters && course.chapters.length > 0) {
        // Multi-chapter ebook - download all chapters
        toast.info(`Downloading ${course.chapters.length} chapters...`);

        course.chapters.forEach((chapter, index) => {
          setTimeout(() => {
            downloadChapterFile(id, chapter._id, { download: '1' })
              .then((response) => {
                const blobUrl = createPdfBlobUrl(response);
                triggerBlobDownload(blobUrl, `${course.title} - Chapter ${index + 1} - ${chapter.title}.pdf`);
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
              })
              .catch((error) => {
                console.error('Chapter download failed:', error);
                toast.error(`Failed to download ${chapter.title}`);
              });
          }, index * 500); // Stagger downloads by 500ms
        });

        toast.success('Downloading all chapters...');
      } else if (course.ebookFile) {
        // Single-file ebook
        const response = await downloadEbookFile(id, { download: '1' });
        const blobUrl = createPdfBlobUrl(response);
        triggerBlobDownload(blobUrl, `${course.title}.pdf`);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

        toast.success('Downloading ebook...');
      } else {
        throw new Error('No ebook file found');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download ebook');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!course) {
    return <div>Course not found</div>;
  }

  return (
    <div className="course-detail-page">
      <div className="course-hero">
        <div className="container">
          <div className="course-hero-content">
            <div className="course-hero-info">
              {course.category === 'sistem' && (
                <Link
                  to={`/sistem/indicator/${course._id}`}
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors font-medium"
                  style={{ textDecoration: 'none', marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  <FaArrowLeft /> Back to Selection
                </Link>
              )}
              <div className="badge-group">
                <span className={`badge badge-${course.level}`}>{course.level}</span>
                {course.courseType === 'ebook' && course.ebookSubcategory && (
                  <span className={`badge badge-ebook badge-${course.ebookSubcategory}`}>
                    {course.ebookSubcategory}
                  </span>
                )}
              </div>
              <h1>{course.title}</h1>
              <p>{course.description}</p>
              {course.courseType === 'ebook' ? (
                <div className="course-stats">
                  <div className="stat">
                    <FaBookOpen />
                    <span>PDF Ebook</span>
                  </div>
                </div>
              ) : (
                <div className="course-stats">
                  <div className="stat">
                    <FaBook />
                    <span>{course.totalLessons} Lessons</span>
                  </div>
                  <div className="stat">
                    <FaClock />
                    <span>{course.totalDuration} min</span>
                  </div>
                </div>
              )}
              <div className="course-actions">
                {isEnrolled ? (
                  <>
                    {course.courseType === 'ebook' ? (
                      <>
                        <button className="btn btn-primary" onClick={handleReadOnline}>
                          <FaBookReader /> Read Online
                        </button>
                        <button className="btn btn-secondary" onClick={handleDownloadEbook}>
                          <FaDownload /> Download
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/courses/${id}/learn`)}
                      >
                        <FaPlay /> Start Learning
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleEnroll}
                    disabled={enrolling}
                  >
                    {course.courseType === 'ebook' ? (
                      <><FaBookOpen /> {enrolling ? 'Enrolling...' : course.price === 0 ? 'Get Free' : `Get for $${course.price}`}</>
                    ) : (
                      <><FaPlay /> {enrolling ? 'Enrolling...' : course.price === 0 ? 'Enroll Free' : `Enroll for $${course.price}`}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="course-content">
          {course.learningObjectives && course.learningObjectives.length > 0 && (
            <section className="course-section">
              <h2>What You'll Learn</h2>
              <ul className="learning-objectives">
                {course.learningObjectives.map((objective, index) => (
                  <li key={index}>
                    <FaCheck /> {objective}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {course.courseType !== 'ebook' && course.modules && course.modules.length > 0 && (
            <section className="course-section">
              <h2>Course Content</h2>
              <div className="curriculum">
                {course.modules.map((module, index) => (
                  <div key={module._id} className="module-item">
                    <div className="module-header" style={{ backgroundColor: '#000000', padding: '1rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ color: '#FFFFFF', margin: 0 }}>Module {index + 1}: {module.title}</h3>
                      <span style={{ color: '#FFFFFF' }}>{module.lessons.length} lessons</span>
                    </div>
                    <p>{module.description}</p>
                    <ul className="lesson-list">
                      {module.lessons.map((lesson) => (
                        <li key={lesson._id}>
                          <FaPlay size={12} />
                          {lesson.title}
                          {lesson.isFree && <span className="free-badge">Free</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {course.requirements && course.requirements.length > 0 && (
            <section className="course-section">
              <h2>Requirements</h2>
              <ul className="requirements-list">
                {course.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      {/* Flipbook Reader Modal */}
      {showFlipbook && (
        <FlipbookReader
          pdfUrl={ebookUrl}
          isOpen={showFlipbook}
          onClose={() => setShowFlipbook(false)}
          courseTitle={course.title}
          courseId={course._id}
          chapters={readerChapters}
          currentChapterIndex={0}
        />
      )}
    </div>
  );
};

export default CourseDetail;
